import React, { useState, useEffect } from 'react';
import {
  Layout,
  Card,
  Form,
  Input,
  Button,
  message,
  Statistic,
  Row,
  Col,
  Descriptions,
  Space,
  Tag,
  Empty
} from 'antd';
import {
  UserOutlined,
  MailOutlined,
  LockOutlined,
  LogoutOutlined,
  UserOutlined as ProfileOutlined,
  SafetyOutlined,
  DesktopOutlined,
  PlayCircleOutlined,
  CalendarOutlined,
  GlobalOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { authService } from '../services/auth';
import { adminService } from '../services/admin';
import LicenseConfigOverview from '../components/LicenseConfigOverview';

const { Header, Content } = Layout;

const UserDashboard: React.FC = () => {
  const [editForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const navigate = useNavigate();

  // Get current user info
  const user = authService.getCurrentUser();

  // Fetch user's license configuration
  const { data: licenseConfig, isLoading: configLoading } = useQuery({
    queryKey: ['userLicenseConfig', user?.id],
    queryFn: () => user ? adminService.getUserLicenseConfig(user.id) : null,
    enabled: !!user,
  });

  useEffect(() => {
    document.title = '用户面板 - 用户管理系统';
    if (user) {
      editForm.setFieldsValue({
        username: user.username,
        email: user.email,
      });
    }
  }, [user, editForm]);

  const getContentAccessTags = (config: any) => {
    if (!config) return null;

    return (
      <Space size="small">
        <Tag color={config.allowVideo ? 'green' : 'red'}>
          <PlayCircleOutlined /> 视频
        </Tag>
        <Tag color={config.allowAudio ? 'green' : 'red'}>
          <DesktopOutlined /> 音频
        </Tag>
        <Tag color={config.allowDocument ? 'green' : 'red'}>
          文档
        </Tag>
      </Space>
    );
  };

  const getTimeRestrictionDisplay = (config: any) => {
    if (!config) return '未配置';

    const { allowPlayStartTime, allowPlayEndTime } = config;
    if (allowPlayStartTime === '00:00' && allowPlayEndTime === '23:59') {
      return <Tag color="green">全天可用</Tag>;
    }
    return <Tag color="blue">{allowPlayStartTime} - {allowPlayEndTime}</Tag>;
  };

  const getWeekdayDisplay = (config: any) => {
    if (!config || !config.allowWeekdays) return '未配置';

    try {
      const weekdays = JSON.parse(config.allowWeekdays);
      const weekdayNames = ['日', '一', '二', '三', '四', '五', '六'];
      if (weekdays.length === 7) return '每天';
      return weekdays.map((day: number) => weekdayNames[day]).join('、');
    } catch {
      return '每天';
    }
  };

  const getRegionDisplay = (config: any) => {
    if (!config) return '无限制';

    const allowed = config.allowedRegions ? JSON.parse(config.allowedRegions || '[]') : [];
    const restricted = config.restrictedRegions ? JSON.parse(config.restrictedRegions || '[]') : [];

    if (allowed.length > 0) {
      return <Tag color="green">允许: {allowed.join(', ')}</Tag>;
    }
    if (restricted.length > 0) {
      return <Tag color="red">限制: {restricted.join(', ')}</Tag>;
    }
    return <Tag color="blue">无限制</Tag>;
  };

  const handleLogout = () => {
    authService.logout();
    navigate('/login');
  };

  const handleProfileUpdate = async (values: any) => {
    setLoading(true);
    try {
      // Update profile logic would go here
      message.success('个人信息更新成功！');
      // In a real app, you would call your API here
    } catch (error: any) {
      message.error(error.response?.data?.message || '更新失败');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (values: any) => {
    setPasswordLoading(true);
    try {
      // Change password logic would go here
      message.success('密码修改成功！');
      passwordForm.resetFields();
      // In a real app, you would call your API here
    } catch (error: any) {
      message.error(error.response?.data?.message || '密码修改失败');
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header style={{ background: '#fff', padding: '0 24px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <ProfileOutlined style={{ fontSize: '24px', marginRight: '16px' }} />
            <span style={{ fontSize: '18px', fontWeight: 'bold' }}>用户管理面板</span>
          </div>
          <Button
            type="primary"
            icon={<LogoutOutlined />}
            onClick={handleLogout}
          >
            退出
          </Button>
        </div>
      </Header>

      <Content style={{ margin: '24px' }}>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Card title="用户概览" bordered={false}>
              <Row gutter={16}>
                <Col span={8}>
                  <Statistic
                    title="用户名"
                    value={user?.username || '未知'}
                    prefix={<UserOutlined />}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="邮箱"
                    value={user?.email || '未知'}
                    prefix={<MailOutlined />}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="角色"
                    value={user?.role === 'admin' ? '管理员' : '普通用户'}
                  />
                </Col>
              </Row>
            </Card>
          </Col>

          {/* License Configuration Section */}
          <Col span={24}>
            <Card
              title={
                <span>
                  <SafetyOutlined style={{ marginRight: 8 }} />
                  许可证配置信息
                </span>
              }
              bordered={false}
              loading={configLoading}
            >
              {licenseConfig ? (
                <Row gutter={[16, 16]}>
                  <Col span={6}>
                    <Statistic
                      title="许可证状态"
                      value={licenseConfig.isActive ? '激活' : '未激活'}
                      valueStyle={{ color: licenseConfig.isActive ? '#3f8600' : '#cf1322' }}
                      prefix={licenseConfig.isActive ? <SafetyOutlined /> : <DesktopOutlined />}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic
                      title="最大设备数"
                      value={licenseConfig.maxDevices || '无限制'}
                      prefix={<DesktopOutlined />}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic
                      title="同时播放数"
                      value={licenseConfig.maxConcurrentPlays || '无限制'}
                      prefix={<PlayCircleOutlined />}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic
                      title="总播放次数"
                      value={licenseConfig.maxPlayCount || '无限制'}
                      prefix={<CalendarOutlined />}
                    />
                  </Col>

                  <Col span={12}>
                    <Descriptions title="内容访问权限" bordered column={1} size="small">
                      <Descriptions.Item label="内容类型">
                        {getContentAccessTags(licenseConfig)}
                      </Descriptions.Item>
                      <Descriptions.Item label="时间限制">
                        {getTimeRestrictionDisplay(licenseConfig)}
                      </Descriptions.Item>
                      <Descriptions.Item label="可用星期">
                        {getWeekdayDisplay(licenseConfig)}
                      </Descriptions.Item>
                    </Descriptions>
                  </Col>

                  <Col span={12}>
                    <Descriptions title="地理与网络限制" bordered column={1} size="small">
                      <Descriptions.Item label="地区限制">
                        {getRegionDisplay(licenseConfig)}
                      </Descriptions.Item>
                      <Descriptions.Item label="IP限制">
                        {licenseConfig.allowedIPs || licenseConfig.restrictedIPs ? '有设置' : '无限制'}
                      </Descriptions.Item>
                      <Descriptions.Item label="许可证期限">
                        {licenseConfig.licenseDays ? `${licenseConfig.licenseDays} 天` : '永久'}
                      </Descriptions.Item>
                    </Descriptions>
                  </Col>
                </Row>
              ) : (
                <Empty
                  description="未找到许可证配置信息"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                >
                  <span style={{ color: '#666' }}>请联系管理员配置许可证权限</span>
                </Empty>
              )}
            </Card>
          </Col>

          <Col span={12}>
            <Card title="修改个人信息" bordered={false}>
              <Form
                form={editForm}
                onFinish={handleProfileUpdate}
                layout="vertical"
              >
                <Form.Item
                  label="用户名"
                  name="username"
                  rules={[{ required: true, message: '请输入用户名' }]}
                >
                  <Input prefix={<UserOutlined />} />
                </Form.Item>

                <Form.Item
                  label="邮箱"
                  name="email"
                  rules={[
                    { required: true, message: '请输入邮箱' },
                    { type: 'email', message: '请输入有效的邮箱地址' }
                  ]}
                >
                  <Input prefix={<MailOutlined />} />
                </Form.Item>

                <Form.Item>
                  <Button type="primary" htmlType="submit" loading={loading}>
                    更新个人信息
                  </Button>
                </Form.Item>
              </Form>
            </Card>
          </Col>

          <Col span={12}>
            <Card title="修改密码" bordered={false}>
              <Form
                form={passwordForm}
                onFinish={handlePasswordChange}
                layout="vertical"
              >
                <Form.Item
                  label="当前密码"
                  name="currentPassword"
                  rules={[{ required: true, message: '请输入当前密码' }]}
                >
                  <Input.Password prefix={<LockOutlined />} />
                </Form.Item>

                <Form.Item
                  label="新密码"
                  name="newPassword"
                  rules={[
                    { required: true, message: '请输入新密码' },
                    { min: 6, message: '密码长度至少为6位' }
                  ]}
                >
                  <Input.Password prefix={<LockOutlined />} />
                </Form.Item>

                <Form.Item
                  label="确认新密码"
                  name="confirmPassword"
                  dependencies={['newPassword']}
                  hasFeedback
                  rules={[
                    { required: true, message: '请确认新密码' },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue('newPassword') === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error('两次输入的密码不一致'));
                      },
                    }),
                  ]}
                >
                  <Input.Password prefix={<LockOutlined />} />
                </Form.Item>

                <Form.Item>
                  <Button type="primary" htmlType="submit" loading={passwordLoading}>
                    修改密码
                  </Button>
                </Form.Item>
              </Form>
            </Card>
          </Col>
        </Row>
      </Content>
    </Layout>
  );
};

export default UserDashboard;